import { inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  getSectionAFormGroup,
  getSectionAUpdateData,
  patchSectionAForm,
  SectionAFormGroup,
} from '@app/components/project-wizard/+helpers/section-a-form.helper';
import {
  getSectionBFormGroup,
  getSectionBUpdateData,
  patchSectionBForm,
  SectionBFormGroup,
} from '@app/components/project-wizard/+helpers/section-b-form.helper';
import {
  getSectionCFormGroup,
  getSectionCUpdateData,
  patchSectionCForm,
  SectionCFormGroup,
} from '@app/components/project-wizard/+helpers/section-c-form.helper';
import {
  getSectionDFormGroup,
  getSectionDUpdateData,
  patchSectionDForm,
  SectionDFormGroup,
} from '@app/components/project-wizard/+helpers/section-d-form.helper';
import {
  getSectionEFormGroup,
  getSectionEUpdateData,
  patchSectionEForm,
  SectionEFormGroup,
} from '@app/components/project-wizard/+helpers/section-e-form.helper';
import {
  getSectionFFormGroup,
  getSectionFUpdateData,
  patchSectionFFormWithProjectSetupFRequest,
  SectionFFormGroup,
} from '@app/components/project-wizard/+helpers/section-f-form.helper';
import {
  getSectionGFormGroup,
  getSectionGUpdateData,
  patchSectionGFormWithProjectSetupGResponse,
  SectionGFormGroup,
} from '@app/components/project-wizard/+helpers/section-g-form.helper';
import {
  getSectionHFormGroup,
  getSectionHUpdateData,
  patchSectionHFormWithProjectSetupHResponse,
  SectionHFormGroup,
} from '@app/components/project-wizard/+helpers/section-h-form.helper';
import {
  getSectionIFormGroup,
  getSectionIUpdateData,
  patchSectionIFormWithProjectSetupIResponse,
  SectionIFormGroup,
} from '@app/components/project-wizard/+helpers/section-i-form.helper';
import {
  getSectionJFormGroup,
  SectionJFormGroup,
} from '@app/components/project-wizard/+helpers/section-j-form.helper';
import { getNewFileFormGroup } from '@app/helpers/form.helper';
import {
  ProjectSetupAResponse,
  ProjectSetupBResponse,
  ProjectSetupCResponse,
  ProjectSetupDResponse,
  ProjectSetupEResponse,
  ProjectSetupFRequest,
  ProjectSetupGResponse,
  ProjectSetupHResponse,
  ProjectSetupIResponse,
  WizardSection,
} from '@app/models/project-setup.models';
import { FileUploadControl } from '@app/models/form.models';
import { FileResponse } from '@app/models/shared.models';
import { FileType } from '@app/models/shared.enums';
import { ProjectSetupService } from '@app/services/project-setup/project-setup.service';
import { GenericStore } from '@app/stores/generic.store';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { catchError, first, Observable, of, switchMap } from 'rxjs';

type SectionDataMap = {
  [WizardSection.A]: SectionAFormGroup;
  [WizardSection.B]: SectionBFormGroup;
  [WizardSection.C]: SectionCFormGroup;
  [WizardSection.D]: SectionDFormGroup;
  [WizardSection.E]: SectionEFormGroup;
  [WizardSection.F]: SectionFFormGroup;
  [WizardSection.G]: SectionGFormGroup;
  [WizardSection.H]: SectionHFormGroup;
  [WizardSection.I]: SectionIFormGroup;
  [WizardSection.J]: SectionJFormGroup;
};

export type SectionFormTypes =
  | SectionAFormGroup
  | SectionBFormGroup
  | SectionCFormGroup
  | SectionDFormGroup
  | SectionEFormGroup
  | SectionFFormGroup
  | SectionGFormGroup
  | SectionHFormGroup
  | SectionIFormGroup
  | SectionJFormGroup;

type SectionDataResponseMap = {
  [WizardSection.A]: ProjectSetupAResponse;
  [WizardSection.B]: ProjectSetupBResponse;
  [WizardSection.C]: ProjectSetupCResponse;
  [WizardSection.D]: ProjectSetupDResponse;
  [WizardSection.E]: ProjectSetupEResponse;
  [WizardSection.F]: ProjectSetupFRequest;
  [WizardSection.G]: ProjectSetupGResponse;
  [WizardSection.H]: ProjectSetupHResponse;
  [WizardSection.I]: ProjectSetupIResponse;
  [WizardSection.J]: unknown;
};

export type SectionFormGroupMap = {
  [K in keyof SectionDataMap]: FormGroup<SectionFormTypes>;
};

interface SectionState<T extends FormGroup<any>> {
  loaded: boolean;
  form: T | null;
}

type WizardState = {
  stepIndex: number;
  [WizardSection.A]: SectionState<FormGroup<SectionAFormGroup>>;
  [WizardSection.B]: SectionState<FormGroup<SectionBFormGroup>>;
  [WizardSection.C]: SectionState<FormGroup<SectionCFormGroup>>;
  [WizardSection.D]: SectionState<FormGroup<SectionDFormGroup>>;
  [WizardSection.E]: SectionState<FormGroup<SectionEFormGroup>>;
  [WizardSection.F]: SectionState<FormGroup<SectionFFormGroup>>;
  [WizardSection.G]: SectionState<FormGroup<SectionGFormGroup>>;
  [WizardSection.H]: SectionState<FormGroup<SectionHFormGroup>>;
  [WizardSection.I]: SectionState<FormGroup<SectionIFormGroup>>;
  [WizardSection.J]: SectionState<FormGroup<SectionJFormGroup>>;
};

export const WizardStore = signalStore(
  { providedIn: 'root' },
  withState<WizardState>(() => ({
    stepIndex: 1,
    A: {
      loaded: false,
      form: null,
    },
    B: {
      loaded: false,
      form: null,
    },
    C: {
      loaded: false,
      form: null,
    },
    D: {
      loaded: false,
      form: null,
    },
    E: {
      loaded: false,
      form: null,
    },
    F: {
      loaded: false,
      form: null,
    },
    G: {
      loaded: false,
      form: null,
    },
    H: {
      loaded: false,
      form: null,
    },
    I: {
      loaded: false,
      form: null,
    },
    J: {
      loaded: false,
      form: null,
    },
  })),
  withMethods((store) => {
    const fb = inject(FormBuilder);
    const svc = inject(ProjectSetupService);
    const genericStore = inject(GenericStore);

    const initDefaults: Record<
      Exclude<WizardSection, WizardSection.Base>,
      (response: any) => any
    > = {
      [WizardSection.A]: (response?: ProjectSetupAResponse) => {
        const form = getSectionAFormGroup(fb);
        if (response !== undefined) {
          patchSectionAForm(
            form,
            response,
            genericStore.nameTitlesEntities(),
          );
          patchState(store, {
            A: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.B]: (response?: ProjectSetupBResponse) => {
        const form = getSectionBFormGroup(fb);
        if (response !== undefined) {
          patchSectionBForm(form, response);
          patchState(store, {
            B: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.C]: (response?: ProjectSetupCResponse) => {
        const form = getSectionCFormGroup(fb);
        if (response !== undefined) {
          patchSectionCForm(form, response);
          patchState(store, {
            C: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.D]: (response?: ProjectSetupDResponse) => {
        const form = getSectionDFormGroup(fb);
        if (response !== undefined) {
          patchSectionDForm(
            form,
            response,
            fb,
            genericStore.nameTitlesEntities(),
          );
          patchState(store, {
            D: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.E]: (response?: ProjectSetupEResponse) => {
        const form = getSectionEFormGroup(fb);
        if (response !== undefined) {
          patchSectionEForm(
            form,
            response,
            genericStore.nameTitlesEntities(),
          );
          patchState(store, {
            E: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.F]: (response?: ProjectSetupFRequest) => {
        const form = getSectionFFormGroup(fb);
        if (response !== undefined) {
          patchSectionFFormWithProjectSetupFRequest(form, response);
          patchState(store, {
            F: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.G]: (response?: ProjectSetupGResponse) => {
        const form = getSectionGFormGroup(fb);
        if (response !== undefined) {
          patchSectionGFormWithProjectSetupGResponse(form, response, fb);
        }
        patchState(store, {
          G: {
            loaded: true,
            form: form,
          },
        });
        return form;
      },
      [WizardSection.H]: (response?: ProjectSetupHResponse) => {
        const form = getSectionHFormGroup(fb);
        if (response !== undefined) {
          patchSectionHFormWithProjectSetupHResponse(form, response);
          patchState(store, {
            H: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.I]: (response?: ProjectSetupIResponse) => {
        const form = getSectionIFormGroup(fb);
        if (response !== undefined) {
          patchSectionIFormWithProjectSetupIResponse(form, response);
          patchState(store, {
            I: {
              loaded: true,
              form: form,
            },
          });
        }
        return form;
      },
      [WizardSection.J]: (
        response?: FileResponse[] | { data: FileResponse[] },
      ) => {
        const form = getSectionJFormGroup(fb);
        const files = Array.isArray(response)
          ? response
          : response?.data ?? [];
        patchSectionJFormFromFiles(form, files, fb);
        patchState(store, {
          J: {
            loaded: true,
            form,
          },
        });
        return form;
      },
    };

    return {
      getSectionForm<P extends Exclude<WizardSection, WizardSection.Base>>(
        section: P,
      ): FormGroup<SectionFormTypes> | null {
        return store[section]?.().form as FormGroup<SectionFormTypes> | null;
      },
      getSection<TSection extends Exclude<WizardSection, WizardSection.Base>>(
        projectId: string,
        section: TSection,
      ): Observable<SectionFormGroupMap[TSection]> {
        const cur = store[section]?.();
        if (cur?.loaded && cur.form) {
          return of(cur.form as SectionFormGroupMap[TSection]);
        }

        if (section === WizardSection.J) {
          return svc
            .getProjectFiles(projectId)
            .pipe(
              first(),
              switchMap((response) => {
                const files = Array.isArray(response)
                  ? response
                  : response?.data ?? [];
                return of(
                  initDefaults[section](files) as SectionFormGroupMap[TSection],
                );
              }),
              catchError((err) => {
                console.error('loadSectionJFiles failed', err);
                const emptyForm = initDefaults[section](undefined) as SectionFormGroupMap[TSection];
                return of(emptyForm);
              }),
            );
        }

        return svc
          .getSection<SectionDataResponseMap[TSection]>(projectId, section)
          .pipe(
            first(),
            switchMap((response) => {
              return of(
                initDefaults[section](
                  response,
                ) as SectionFormGroupMap[TSection],
              );
            }),
            catchError((err) => {
              console.error('loadSection failed', err);
              const emptyForm = initDefaults[section](undefined) as SectionFormGroupMap[TSection];
              return of(emptyForm);
            }),
          );
      },

      updateSection<P extends Exclude<WizardSection, WizardSection.Base>>(
        section: P,
        form: SectionFormGroupMap[P],
      ) {
        const cur = store[section]?.();
        if (!cur) return;
        patchState(store, {
          [section]: {
            ...cur,
            data: form.value,
            form,
          },
        });
      },

      saveSection<P extends Exclude<WizardSection, WizardSection.Base>>(
        projectId: string,
        section: P,
        form: SectionFormGroupMap[P],
      ): Observable<any> {
        const cur = store[section]?.();
        if (!cur) {
          console.warn(`Cannot save,* section ${section} not loaded`);
          return of(undefined);
        }
        const newState = { loaded: true, form };
        patchState(store, { [section]: newState });

        const data = getWizardFormDataForSave(form, section);
        if (data === null) {
          return of(undefined);
        }

        return svc.updateSection(projectId, section, data).pipe(
          first(),
          catchError((err) => {
            console.error('postSection failed', err);
            throw err;
          }),
        );
      },
    };
  }),
);

function patchSectionJFormFromFiles(
  form: FormGroup<SectionJFormGroup>,
  files: FileResponse[],
  fb: FormBuilder,
): void {
  form.setControl(
    'companyExtracts',
    fileArrayFromFiles(fb, files, FileType.documentCompanyRegistration),
  );
  form.setControl(
    'technicalUpload',
    fileArrayFromFiles(fb, files, FileType.documentTechnicalDescription),
  );
  form.setControl('valuation', fileArrayFromFiles(fb, files, FileType.documentValuation));
  form.setControl('mainImage', singleFileArrayFromFiles(fb, files, FileType.mainImage));
  form.setControl('otherDocs', fileArrayFromFiles(fb, files, FileType.document));
  form.setControl('images', fileArrayFromFiles(fb, files, FileType.image));
  form.setControl('videos', fileArrayFromFiles(fb, files, FileType.video));
}

function singleFileArrayFromFiles(
  fb: FormBuilder,
  files: FileResponse[],
  fileType: FileType,
): FormArray<FormGroup<FileUploadControl>> {
  const firstMatch = files.find((file) => file.fileType === fileType);
  if (!firstMatch) {
    return fb.nonNullable.array<FormGroup<FileUploadControl>>([
      getNewFileFormGroup(fb, fileType, false, true),
    ]);
  }

  const group = getNewFileFormGroup(fb, fileType, false, true);
  group.controls.id.setValue(firstMatch.id);
  group.controls.title.setValue(firstMatch.name ?? null);
  group.controls.description.setValue(firstMatch.description ?? null);
  return fb.nonNullable.array<FormGroup<FileUploadControl>>([group]);
}

function fileArrayFromFiles(
  fb: FormBuilder,
  files: FileResponse[],
  fileType: FileType,
): FormArray<FormGroup<FileUploadControl>> {
  const matchingFiles = files.filter((file) => file.fileType === fileType);
  const controls = matchingFiles.map((file) => {
    const g = getNewFileFormGroup(fb, fileType, false, true);
    g.controls.id.setValue(file.id);
    g.controls.title.setValue(file.name ?? null);
    g.controls.description.setValue(file.description ?? null);
    return g;
  });
  if (!controls.length) {
    controls.push(getNewFileFormGroup(fb, fileType, false, true));
  }
  return fb.nonNullable.array<FormGroup<FileUploadControl>>(controls);
}

function getWizardFormDataForSave(
  form: FormGroup<SectionFormTypes>,
  section: WizardSection
): any {
  switch (section) {
    case WizardSection.A:
      return getSectionAUpdateData(form as FormGroup<SectionAFormGroup>);
    case WizardSection.B:
      return getSectionBUpdateData(form as FormGroup<SectionBFormGroup>);
    case WizardSection.C:
      return getSectionCUpdateData(form as FormGroup<SectionCFormGroup>);
    case WizardSection.D:
      return getSectionDUpdateData(form as FormGroup<SectionDFormGroup>);
    case WizardSection.E:
      return getSectionEUpdateData(form as FormGroup<SectionEFormGroup>);
    case WizardSection.F:
      return getSectionFUpdateData(form as FormGroup<SectionFFormGroup>);
    case WizardSection.G:
      return getSectionGUpdateData(form as FormGroup<SectionGFormGroup>);
    case WizardSection.H:
      return getSectionHUpdateData(form as FormGroup<SectionHFormGroup>);
    case WizardSection.I:
      return getSectionIUpdateData(form as FormGroup<SectionIFormGroup>);
    case WizardSection.J:
      return null;
    default:
      return null;
  }
}
